import { Fragment } from 'react';
import { tv } from 'tailwind-variants';

import { fontStyles } from '@/styles/typography';

import { ChevronDownIcon } from '@/ui/Icons/ChevronDownIcon';

const pageStyles = tv({
  base: [
    fontStyles({ variant: 'LABEL_MD' }),
    'items-center',
    'rounded-sm',
    'border-1',
    'shrink-0',
    'inline-flex',
    'justify-center',
    'h-9',
    'w-9',
    'px-2',
  ],
  variants: {
    active: {
      true: [
        'bg-token-purple-300',
        'shadow-purple',
        'text-token-purple-1100',
        'cursor-default',
        'border-transparent',
      ],
      false: [
        'bg-token-gray-200',
        'text-token-gray-1600',
        'border-transparent',
        'cursor-pointer',
        'hover:bg-token-gray-300',
        'active:bg-token-gray-400',
        'focus:outline-3',
        'focus:outline-token-gray-500',
        'disabled:cursor-default',
        'disabled:opacity-50',
      ],
    },
  },
});

interface PaginationProps {
  lastPage: number;
  currentPage: number;
  maxPageSectionSize?: number;
  onFirst: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onLast: (lastPage: number) => void;
  onPageChange: (page: number) => void;
  pageSection: (lastPage: number) => number[];
}

const MINIMUM_PAGE_COUNT = 3;

export const Pagination = ({
  lastPage,
  currentPage,
  onFirst,
  onPrevious,
  onNext,
  onLast,
  onPageChange,
  pageSection,
}: PaginationProps) => {
  const isSinglePage = lastPage === 1;
  const isSimplePagination = lastPage <= MINIMUM_PAGE_COUNT;

  if (isSinglePage) {
    return null;
  }

  if (isSimplePagination) {
    return (
      <div className="gap-x-1.5 flex select-none">
        {Array.from({ length: lastPage }, (_, i) => i + 1).map((page) =>
          page === currentPage ? (
            <Fragment key={page}>
              <div className={pageStyles({ active: true })}>{page}</div>
            </Fragment>
          ) : (
            <Fragment key={page}>
              <button
                onClick={() => onPageChange(page)}
                disabled={page === currentPage}
                className={pageStyles({ active: false })}
              >
                {page}
              </button>
            </Fragment>
          ),
        )}
      </div>
    );
  }

  const thisSection = pageSection(lastPage);

  const showFirstWithEllipsis = !thisSection.includes(1);
  const showLastWithEllipsis = !thisSection.includes(lastPage);
  const ellipsisElement = (
    <button className={pageStyles({})} disabled>
      …
    </button>
  );

  const renderPageNumber = (page: number) => {
    const isActivePage = page === currentPage;

    return (
      <button
        key={page}
        onClick={() => {
          if (!isActivePage) onPageChange(page);
        }}
        className={pageStyles({ active: isActivePage })}
      >
        {page}
      </button>
    );
  };

  return (
    <div className="gap-x-1.5 flex select-none">
      <button disabled={currentPage === 1} onClick={onPrevious} className={pageStyles({})}>
        <ChevronDownIcon className="rotate-90" />
      </button>

      {showFirstWithEllipsis && (
        <>
          <button onClick={onFirst} className={pageStyles({ active: currentPage === 1 })}>
            1
          </button>
          {ellipsisElement}
        </>
      )}

      {thisSection.map(renderPageNumber)}

      {showLastWithEllipsis && (
        <>
          {ellipsisElement}
          <button
            onClick={() => onLast(lastPage)}
            className={pageStyles({ active: currentPage === lastPage })}
          >
            {lastPage}
          </button>
        </>
      )}

      <button disabled={currentPage === lastPage} onClick={onNext} className={pageStyles({})}>
        <ChevronDownIcon className="-rotate-90" />
      </button>
    </div>
  );
};
